import { FC, useEffect, useState } from 'react'
import {
  Button, DeleteWithConfirmButton, EditButton,
  Show, Tab, TabbedShowLayout, TopToolbar,
  useGetList, useRecordContext, useTranslate,
} from 'react-admin'
import { getJson, getMcpBaseUrl, getUrl } from '../../providers/data'
import EditNoteIcon from '@mui/icons-material/EditNote'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { Box, Chip, Typography } from '@mui/material'
import {
  CopyBlock, DetailCard, InfoRow, LogsChart, StatCard, StatusChip,
  WorkflowLinksDatagrid, formatDateTime,
} from '../../components'
import { fadeSlideIn } from '../../components/dashboard'
import { xxllncColor } from '../../layout/theme/color'
import {
  WorkflowExecutionOut, WorkflowNode, WorkflowOut, WorkflowUsageSummaryOut,
} from '../../types/apiRequests'
import { useNavigate } from 'react-router-dom'
import WorkflowVariablesSection from './WorkflowVariablesSection'
import { STATUS_COLORS } from './workflowUtils'

const EUR_4 = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

const ShowActions: FC = () => {
  const record = useRecordContext<WorkflowOut>()
  const translate = useTranslate()
  const navigate = useNavigate()

  return (
    <TopToolbar>
      {record && (
        <Button
          label={translate('workflow.actions.editFlow')}
          onClick={() => navigate(`/workflow/${record.id}/editor`)}
        >
          <EditNoteIcon />
        </Button>
      )}
      <EditButton />
      <DeleteWithConfirmButton
        confirmTitle={translate('delete.confirmTitle')}
        confirmContent={translate('delete.confirmContentWorkflow')}
      />
    </TopToolbar>
  )
}

const formatCost = (record: WorkflowOut, translate: (k: string) => string) => {
  if (record.cost_per_workflow_execution != null) {
    return EUR_4.format(record.cost_per_workflow_execution)
  }
  if (record.effective_cost_per_workflow_execution != null) {
    return `${EUR_4.format(record.effective_cost_per_workflow_execution)} ${translate(
      'workflow.list.costPortalDefault',
    )}`
  }
  return translate('workflow.list.costNotSet')
}

const DetailsTab: FC = () => {
  const record = useRecordContext<WorkflowOut>()
  const translate = useTranslate()

  // Use /usage_summary for fixed-size aggregates instead of fetching raw executions (simplerest caps lists at 100).
  const [summary, setSummary] = useState<WorkflowUsageSummaryOut | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  useEffect(() => {
    if (!record?.id) return undefined
    let cancelled = false
    setSummaryLoading(true)
    void getJson<WorkflowUsageSummaryOut>(
      `${getUrl()}/workflow/${record.id}/usage_summary?period=30d`,
    )
      .then((data) => {
        if (!cancelled) setSummary(data)
      })
      .catch(() => {
        if (!cancelled) setSummary(null)
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [record?.id])

  const { total: linkCount } = useGetList(
    'organization_workflow_link',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { workflow_id: record?.id } },
    { enabled: !!record?.id },
  )

  // Expand each bucket into N entries (total/errors) — LogsChart re-buckets time internally.
  const chartData = (summary?.buckets ?? []).flatMap((bucket) => {
    const total = bucket.total ?? 0
    const errors = bucket.errors ?? 0
    const successCount = Math.max(total - errors, 0)
    return [
      // eslint-disable-next-line camelcase
      ...Array.from({ length: successCount }, () => ({ created: bucket.date, is_success: true })),
      // eslint-disable-next-line camelcase
      ...Array.from({ length: errors }, () => ({ created: bucket.date, is_success: false })),
    ]
  })
  const execsLoading = summaryLoading

  const stats = {
    total: summary?.total ?? 0,
    successful: summary?.successful ?? 0,
    failed: summary?.failed ?? 0,
    successRate: summary?.success_rate == null ? null : Math.round(summary.success_rate),
  }

  if (!record) return null

  const triggerNode = (record.nodes as WorkflowNode[] | undefined)?.find(
    (n) => n.type === 'trigger',
  )
  const resolvedTrigger =
    (triggerNode && String(triggerNode.data?.triggerType ?? '')) || record.trigger_type || ''
  const triggerLabel = resolvedTrigger || '—'
  const isMcpTrigger = resolvedTrigger.toLowerCase() === 'mcp'

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#1a1a1a',
            }}
          >
            {record.display_name}
          </Typography>
          <StatusChip active={record.enabled} />
          <Chip
            label={triggerLabel}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              height: 22,
              border: 'none',
              bgcolor: xxllncColor.opDark10,
              color: xxllncColor.dark100,
              textTransform: 'capitalize',
            }}
          />
          {record.auto_approve && (
            <Chip
              label={translate('workflow.details.autoApprove')}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                border: 'none',
                bgcolor: xxllncColor.opGreen10,
                color: xxllncColor.green100,
              }}
            />
          )}
        </Box>
        {record.description && (
          <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, maxWidth: 600 }}>
            {record.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        <StatCard
          value={record.executions_24h ?? 0}
          label={translate('workflow.stat.executions24h')}
          accent={xxllncColor.dark100}
          delay={80}
        />
        <StatCard
          value={linkCount ?? record.link_count ?? 0}
          label={translate('workflow.stat.links')}
          accent={xxllncColor.orange100}
          delay={120}
        />
        <StatCard
          value={stats.total}
          label={translate('workflow.stat.executionsTotal')}
          accent={xxllncColor.dark100}
          delay={160}
        />
        <StatCard
          value={stats.successRate ?? 0}
          label={
            stats.successRate == null
              ? translate('workflow.stat.successRateNoData')
              : translate('workflow.stat.successRate')
          }
          accent={stats.failed > 0 ? xxllncColor.red100 : xxllncColor.green100}
          delay={200}
        />
      </Box>

      {/* Status === 'completed' counts as success; anything else as a failure. */}
      <Box sx={{ mb: 3 }}>
        <LogsChart data={chartData} isLoading={execsLoading} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <DetailCard title={translate('data.details')} delay={240}>
          <InfoRow label={translate('workflow.details.name')} value={record.name} mono />
          <InfoRow label={translate('workflow.details.triggerType')} value={triggerLabel} />
          {isMcpTrigger && (
            <Box sx={{ py: 1.25, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography sx={{
                fontSize: 12, fontWeight: 500, color: 'rgba(0,0,0,0.45)',
                textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1,
              }}>
                {translate('data.connectUrl')}
              </Typography>
              <CopyBlock content={`${getMcpBaseUrl()}/api/v1/mcp/workflow/${record.name}`} />
            </Box>
          )}
          <InfoRow label={translate('workflow.details.nodeCount')} value={String((record.nodes || []).length)} />
          <InfoRow label={translate('workflow.details.edgeCount')} value={String((record.edges || []).length)} />
          <InfoRow label={translate('workflow.list.cost')} value={formatCost(record, translate)} />
          <InfoRow label={translate('data.createdAt')} value={formatDateTime(record.created)} />
          <InfoRow label={translate('data.lastModified')} value={formatDateTime(record.lastModified)} />
        </DetailCard>

        {record.about ? (
          <DetailCard title={translate('workflow.details.about')} delay={280}>
            <Typography
              sx={{
                fontSize: 13,
                color: '#1a1a1a',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {record.about}
            </Typography>
          </DetailCard>
        ) : (
          <DetailCard title={translate('workflow.stat.successRateTitle')} delay={280}>
            <InfoRow
              label={translate('workflow.stat.executionsSuccessful')}
              value={String(stats.successful)}
            />
            <InfoRow
              label={translate('workflow.stat.executionsFailed')}
              value={String(stats.failed)}
            />
            <InfoRow
              label={translate('workflow.stat.successRate')}
              value={stats.successRate == null ? '—' : `${stats.successRate}%`}
            />
          </DetailCard>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <WorkflowVariablesSection />
      </Box>
    </Box>
  )
}

const LinksTab: FC = () => {
  const record = useRecordContext<WorkflowOut>()

  if (!record) return null

  return (
    <WorkflowLinksDatagrid
      // eslint-disable-next-line camelcase
      filter={{ workflow_id: record.id }}
      // eslint-disable-next-line camelcase
      createState={{ workflow_id: record.id }}
      showOrganization
    />
  )
}

const ExecutionsTab: FC = () => {
  const record = useRecordContext<WorkflowOut>()
  const translate = useTranslate()
  const navigate = useNavigate()

  const { data, isLoading } = useGetList<WorkflowExecutionOut>(
    'workflow_execution',
    {
      pagination: { page: 1, perPage: 25 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { workflow_id: record?.id },
    },
    { enabled: !!record?.id },
  )

  if (!record) return null

  if (isLoading) return <Typography color="text.secondary">{translate('workflow.loading')}</Typography>

  if (!data || data.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 2 }}>{translate('workflow.tabs.noExecutions')}</Typography>
  }

  return (
    <Box>
      {data.map((execution) => (
        <Box
          key={execution.id}
          onClick={() => navigate(`/workflow_execution/${execution.id}/show`)}
          sx={{
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'py': 1.25,
            'px': 1,
            'borderBottom': '1px solid rgba(0,0,0,0.05)',
            'cursor': 'pointer',
            'borderRadius': '8px',
            'transition': 'background 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              icon={
                execution.status === 'completed' ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : execution.status === 'failed' ? (
                  <ErrorIcon sx={{ fontSize: 14 }} />
                ) : undefined
              }
              label={translate(`workflow.execution.${execution.status}`)}
              size="small"
              color={STATUS_COLORS[execution.status] ?? 'default'}
              variant="outlined"
              sx={{ height: 24, fontSize: 11, textTransform: 'capitalize' }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {execution.trigger_type}
              {(() => {
                const triggerStep = execution.steps?.find((s) => s.node_type === 'trigger')
                const label = triggerStep?.node_label || triggerStep?.node_id
                return label ? (
                  <Typography component="span" sx={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', ml: 0.75 }}>
                    {label}
                  </Typography>
                ) : null
              })()}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {execution.triggered_by}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {execution.organization_display_name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {formatDateTime(execution.created)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const WorkflowShow: FC = () => (
  <Show actions={<ShowActions />}>
    <TabbedShowLayout>
      <Tab label="workflow.tabs.details">
        <DetailsTab />
      </Tab>
      <Tab label="workflow.tabs.links" path="links">
        <LinksTab />
      </Tab>
      <Tab label="workflow.tabs.executions" path="executions">
        <ExecutionsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
)

export default WorkflowShow
